import { Group, Table, Text, VisuallyHidden } from '@mantine/core';
import {
  IconBuildingFactory,
  IconFile3d,
  IconFileTypeCsv,
  IconFileTypeDocx,
  IconFileTypeJpg,
  IconFileTypePdf,
  IconFileTypePng,
  IconFileTypeXls,
  IconFileUnknown,
  IconLink,
  IconRuler2,
} from '@tabler/icons-react';
import React, { ReactNode } from 'react';

import { formatDate } from 'src/utils';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { DownloadActionIcon } from 'src/design/atoms/DownloadActionIcon';
import { InlineTextInput } from 'src/design/molecules/InlineTextInput';
import { IAttachedFile } from 'src/design/organisms/attachments/attachmentTypes';

const IconLookup: Record<string, ReactNode> = {
  csv: <IconFileTypeCsv size={20} />,
  pdf: <IconFileTypePdf size={20} />,
  xlsx: <IconFileTypeXls size={20} />,
  cfb: <IconFileTypeXls size={20} />,
  xls: <IconFileTypeXls size={20} />,
  png: <IconFileTypePng size={20} />,
  jpg: <IconFileTypeJpg size={20} />,
  jpeg: <IconFileTypeJpg size={20} />,
  docx: <IconFileTypeDocx size={20} />,
  ifc: <IconFile3d size={20} />,
  fab: <IconBuildingFactory size={20} />,
  cad: <IconRuler2 size={20} />,
};

const fileIcon = (type?: string | null): ReactNode =>
  IconLookup[type ?? ''] ?? <IconFileUnknown size={20} />;

export interface IFileTableProps {
  files: IAttachedFile[];
  emptyText: string;
  /** Adds the Version column (Versions tab). */
  showVersion?: boolean;
  onDownload: (file: IAttachedFile) => void;
  /** Omit to render the table without delete controls (read-only / disabled). */
  onRemove?: (file: IAttachedFile) => void;
  /** When set, files carrying an `externalUrl` get a "Copy link" action. */
  onCopyLink?: (file: IAttachedFile) => void;
  /** When set, renders a "Description" column with an inline editor bound to each file's comment. */
  onCommentSave?: (
    file: IAttachedFile,
    comment: string,
  ) => void | Promise<void>;
}

/** Internal to FileDrop — renders either the attachments or versions table with the same columns/behavior. */
export const FileTable: React.FC<IFileTableProps> = ({
  files,
  emptyText,
  showVersion = false,
  onDownload,
  onRemove,
  onCommentSave,
  onCopyLink,
}) => {
  if (files.length === 0) {
    return (
      <Text c="dimmed" mt="md">
        {emptyText}
      </Text>
    );
  }

  return (
    <Table striped highlightOnHover mt="md">
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={80}>
            <VisuallyHidden>Actions</VisuallyHidden>
          </Table.Th>
          <Table.Th w={80}>
            <VisuallyHidden>File type</VisuallyHidden>
          </Table.Th>
          {showVersion && <Table.Th w={80}>Version</Table.Th>}
          <Table.Th w={800}>Name</Table.Th>
          {onCommentSave && <Table.Th w={300}>Description</Table.Th>}
          <Table.Th w={100}>Size</Table.Th>
          <Table.Th w={120}>Date Added</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {files.map((file) => (
          <Table.Tr key={file.id}>
            <Table.Td w={80}>
              <Group gap="xs" wrap="nowrap">
                <DownloadActionIcon
                  label={`Download ${file.name ?? 'file'}`}
                  onClick={() => onDownload(file)}
                />
                {onCopyLink && file.externalUrl && (
                  <BasicActionIcon
                    label={`Copy link for ${
                      file.name ?? 'file'
                    } — anyone with it can view until revoked`}
                    onClick={() => onCopyLink(file)}
                  >
                    <IconLink size={18} />
                  </BasicActionIcon>
                )}
                {onRemove && (
                  <DeleteActionIcon
                    requiredConfirmation
                    label={`Delete ${file.name ?? 'file'}`}
                    onDelete={() => onRemove(file)}
                  />
                )}
              </Group>
            </Table.Td>
            <Table.Td w={80} ta="center">
              {fileIcon(file.type)}
            </Table.Td>
            {showVersion && <Table.Td w={80}>{file.version}</Table.Td>}
            <Table.Td w={800}>{file.name}</Table.Td>
            {onCommentSave && (
              <Table.Td w={300}>
                <InlineTextInput
                  initialValue={file.comment ?? ''}
                  onSave={(value) => onCommentSave(file, value)}
                  aria-label={`Description for ${file.name ?? 'file'}`}
                />
              </Table.Td>
            )}
            <Table.Td w={100}>
              {Math.round((file.size ?? 0) / 1000)} KB
            </Table.Td>
            <Table.Td w={120}>
              {formatDate(file.createdAt, 'DD MMM YYYY')}
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};
